import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LoanImportResult, LoanImportSummary } from '@app/shared';
import { hashFile, importLoansChunked, listLoanImports, type ImportFileInfo } from '../api';
import { parseCsv, readWorkbook, rowsToPreview, type Preview } from '../import/extract';
import { PartialImportError, type ImportProgress } from '../import/chunkedImport';

export function ImportPage() {
  const [csvText, setCsvText] = useState('');
  const [sheetRows, setSheetRows] = useState<string[][] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<LoanImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  // What was uploaded, recorded with the import so the history can name it. Held from the
  // moment the file is read, because that is the only point the bytes are in hand.
  const [fileInfo, setFileInfo] = useState<ImportFileInfo>({});
  const [history, setHistory] = useState<LoanImportSummary[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await listLoanImports());
      setHistoryError(null);
    } catch (e) {
      setHistoryError(String(e));
    }
  }, []);

  useEffect(() => { void loadHistory(); }, [loadHistory]);

  // An uploaded workbook takes precedence over pasted CSV.
  const preview = useMemo<Preview | null>(() => {
    if (sheetRows) return rowsToPreview(sheetRows);
    if (!csvText.trim()) return null;
    return rowsToPreview(parseCsv(csvText));
  }, [csvText, sheetRows]);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const buf = await file.arrayBuffer();
      setSheetRows(readWorkbook(buf));
      setFileName(file.name);
      setFileInfo({ fileName: file.name, fileSize: file.size, fileHash: await hashFile(buf) });
      setCsvText('');
    } catch (e) {
      setError(`Could not read workbook: ${String(e)}`);
    }
  };

  const clearFile = () => { setSheetRows(null); setFileName(null); setFileInfo({}); };

  const handleImport = async () => {
    if (!preview || 'error' in preview) return;
    setImporting(true);
    setError(null);
    setResult(null);
    setProgress({ done: 0, total: preview.rows.length });
    try {
      setResult(await importLoansChunked(preview.rows, fileInfo, setProgress));
    } catch (e) {
      // A failed chunk doesn't undo the ones before it, so show what landed
      // alongside the error rather than only the error.
      if (e instanceof PartialImportError) {
        setResult(e.partial);
        setError(`${e.message} — the rows already imported are listed below. `
          + 'Re-importing the same file is safe: loans already in the system are skipped.');
      } else {
        setError(String(e));
      }
    } finally {
      setImporting(false);
      setProgress(null);
      // Whatever happened, it is now part of the history — including a run that failed
      // partway, which is the one worth being able to see.
      void loadHistory();
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Loan Manager Extract Import</h1>
      </div>

      <p className="muted">
        Upload the monthly PCFS Loan Manager export (<code>.xlsx</code>) — or paste it as CSV.
        Required columns: <code>Loan Nbr</code> (UMWSB #) and <code>Short Name</code> (borrower).
        Loans are matched on <strong>Loan Nbr</strong>: new loans are added as <strong>Active</strong>;
        loans already in the system are skipped (the import never overwrites existing records).
      </p>

      {error && <div className="error-banner">{error}</div>}

      <section className="card">
        <h2>Upload Workbook</h2>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={e => handleFile(e.target.files?.[0])}
        />
        {fileName && (
          <p className="muted">
            Loaded <strong>{fileName}</strong>.{' '}
            <button className="btn-sm" onClick={clearFile}>Clear</button>
          </p>
        )}
      </section>

      {!sheetRows && (
        <section className="card">
          <h2>…or paste CSV</h2>
          <textarea
            rows={10}
            className="csv-input"
            placeholder="Loan Nbr,Sba Number,Short Name,Record Type - Code,Loan Amt,Part Bal (Bor),Sba Grty %,Note Date,Maturity Date,Naics Code,Site State,DATEINLIQUID,Repurchase Date,USER18D,USER20D
123456,1234567890,Jose Rossi LLC,2,150000,-105525.63,85,9/15/2023,9/15/2033,445132,IL,0,0,0,0"
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
          />
        </section>
      )}

      {preview && (
        <section className="card">
          <h2>Preview</h2>
          {'error' in preview ? (
            <div className="error-banner">{preview.error}</div>
          ) : (
            <>
              <p className="muted">{preview.rows.length} valid row{preview.rows.length === 1 ? '' : 's'} detected.</p>
              {/* A row missing Loan Nbr or Short Name can't be imported and is dropped here
                  rather than sent and skipped, so say so -- otherwise the count just looks
                  short. Entirely blank rows are not counted: the extract's sheet declares a
                  far larger range than its data, so thousands of them are normal. */}
              {preview.dropped > 0 && (
                <p className="muted">
                  {preview.dropped} row{preview.dropped === 1 ? '' : 's'} skipped — missing Loan Nbr or Short Name.
                </p>
              )}
              {preview.unmapped.length > 0 && (
                <p className="muted">
                  Unrecognised column{preview.unmapped.length === 1 ? '' : 's'} ignored: {preview.unmapped.join(', ')}.
                </p>
              )}
              <div className="table-wrapper">
                <table className="loans-table">
                  <thead>
                    <tr>
                      <th>UMWSB #</th>
                      <th>SBA #</th>
                      <th>Borrower</th>
                      <th>Type</th>
                      <th>Loan Amt</th>
                      <th>Gty Bal</th>
                      <th>Gty %</th>
                      <th>State</th>
                      <th>NAICS</th>
                      <th>Note Date</th>
                      <th>Purchase Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 10).map((r, i) => (
                      <tr key={i}>
                        <td className="mono">{r.umwsbLoanNumber}</td>
                        <td className="mono">{r.sbaLoanNumber ?? '—'}</td>
                        <td>{r.borrowerName}</td>
                        <td>{r.loanType ?? '—'}</td>
                        <td className="money">{r.approvalGross ?? '—'}</td>
                        <td className="money">{r.guarantyBalance ?? '—'}</td>
                        <td>{r.guarantyPct ?? '—'}</td>
                        <td>{r.stateLocated ?? '—'}</td>
                        <td className="mono">{r.naicsCode ?? '—'}</td>
                        <td>{r.noteDate ?? '—'}</td>
                        <td>{r.purchaseDate ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.rows.length > 10 && <p className="muted">… and {preview.rows.length - 10} more.</p>}
              <button
                className="btn-primary"
                onClick={handleImport}
                disabled={importing || preview.rows.length === 0}
              >
                {importing ? 'Importing…' : `Import ${preview.rows.length} row${preview.rows.length === 1 ? '' : 's'}`}
              </button>
              {/* The extract is sent in batches, so there is a real count to show
                  instead of an indeterminate spinner on a minute-long request. */}
              {progress && (
                <p className="muted" role="status" aria-live="polite">
                  Imported {progress.done.toLocaleString()} of {progress.total.toLocaleString()} rows…
                </p>
              )}
            </>
          )}
        </section>
      )}

      {result && (
        <section className="card">
          <h2>Result</h2>
          <ul>
            <li><strong>Inserted:</strong> {result.inserted}</li>
            <li><strong>Skipped (already known / invalid):</strong> {result.skipped}</li>
            <li><strong>Errors:</strong> {result.errors.length}</li>
          </ul>
          {result.errors.length > 0 && (
            <table className="audit-table">
              <thead>
                <tr><th>UMWSB #</th><th>Error</th></tr>
              </thead>
              <tbody>
                {result.errors.map((e, i) => (
                  <tr key={i}><td className="mono">{e.umwsbLoanNumber}</td><td>{e.message}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <section className="card">
        <div className="section-header">
          <h2>Import history</h2>
          <button className="btn-sm" onClick={() => void loadHistory()}>Refresh</button>
        </div>

        {historyError && <div className="error-banner">{historyError}</div>}
        {history === null && !historyError && <p className="muted">Loading…</p>}
        {history?.length === 0 && <p className="muted">No extract has been imported yet.</p>}

        {history && history.length > 0 && (
          <table className="audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>File</th>
                <th>By</th>
                <th className="num">Rows</th>
                <th className="num">Inserted</th>
                <th className="num">Skipped</th>
                <th className="num">Errors</th>
                <th className="num">Loans</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id}>
                  <td>{new Date(h.startedAt).toLocaleString()}</td>
                  <td>
                    {/* The hash is what identifies the file; show enough to compare by eye
                        and the whole thing on hover. */}
                    <span title={h.fileHash ? `SHA-256 ${h.fileHash}` : undefined}>
                      {h.fileName ?? <span className="muted">pasted CSV</span>}
                    </span>
                    {h.fileSize != null && (
                      <> <span className="muted">({(h.fileSize / 1048576).toFixed(1)} MB)</span></>
                    )}
                  </td>
                  <td>{h.importedByName ?? '—'}</td>
                  <td className="num">{h.totalRows ?? '—'}</td>
                  <td className="num">{h.inserted}</td>
                  <td className="num">{h.skipped}</td>
                  <td className="num">{h.errorCount > 0 ? <strong>{h.errorCount}</strong> : 0}</td>
                  {/* Lower than Inserted means loans have been deleted since. */}
                  <td className="num">{h.loansFromImport}</td>
                  <td>{importStatus(h)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/**
 * How an import ended, in one word.
 *
 * "Incomplete" is the state worth surfacing: the client says up front how many batches a
 * file will be sent as, so fewer arriving means the run stopped partway and some of the
 * file was never offered. Those rows are simply absent — no error was recorded for them,
 * because nothing ever tried to insert them. Re-running the file finishes the job.
 */
function importStatus(h: LoanImportSummary) {
  if (h.completedAt === null && h.batchCount !== null && h.batchesReceived < h.batchCount) {
    return (
      <span className="status-badge status-pending"
        title={`${h.batchesReceived} of ${h.batchCount} batches received — re-import the file to finish it`}>
        Incomplete
      </span>
    );
  }
  if (h.errorCount > 0) {
    return <span className="status-badge status-pending">Completed with errors</span>;
  }
  return <span className="status-badge status-active">Completed</span>;
}
